import Decimal from 'decimal.js'
import { type GetArgumentType } from './custom_type_guard'

const _baseDecimalize = (
  value: string | number | undefined,
  options?: {
    toPrecise?: number
    toFixed?: number
    rawNumber?: boolean
    toDecimalPlace?: number
    roundingMode?: `ROUND_HALF_UP` | `ROUND_HALF_DOWN` | `ROUND_UP` | `ROUND_DOWN`
  },
) => {
  const { rawNumber, toPrecise, toDecimalPlace, roundingMode, toFixed } = { ...options }
  try {
    if (!value)
      return new Decimal(0)
    // If undefined, return 0 instead

    const floatNumber = Number.parseFloat(value.toString())

    if (Number.isNaN(floatNumber))
      throw new TypeError(`${ value } cannot be number!`)
    // If unknown number input throw error

    let dec = new Decimal(`${ floatNumber }`)

    // Handle rawNumber
    if (rawNumber)
      return dec.toNumber()

    const rm = roundingMode ?? `ROUND_HALF_UP`

    // Handle toDecimalPlaces
    dec = dec.toDP(toDecimalPlace ?? 12, Decimal[rm])

    // Handle toPrecise
    if (toPrecise)
      dec = new Decimal(dec.toPrecision(toPrecise ?? 12, Decimal[rm]))

    // Handle toPrecise
    if (toFixed)
      return dec.toFixed(toFixed ?? 6, Decimal[rm])

    return dec
  } catch (error) {
    return void 0
  }
}

export const normalizeFloat = (
  value: GetArgumentType<typeof _baseDecimalize>[0],
  options?: GetArgumentType<typeof _baseDecimalize>[1],
): number | string => {
  try {
    const dec = _baseDecimalize(value, options)
    if (!dec)
      throw new Error(`dec is undefined!`)
    if (dec instanceof Decimal)
      return dec.toNumber()

    return dec
  } catch (error) {
    throw new Error(`NormalizeFloat error: ${ error.message }, stack: ${ error.stack }`)
  }
}

export const normalizeFloatBase = (
  value: GetArgumentType<typeof _baseDecimalize>[0],
  options?: GetArgumentType<typeof _baseDecimalize>[1],
): Decimal => {
  try {
    const dec = _baseDecimalize(value, options)
    if (!dec)
      throw new Error(`dec is undefined!`)

    return new Decimal(`${ dec }`)
  } catch (error) {
    throw new Error(`NormalizeFloat error: ${ error.message }, stack: ${ error.stack }`)
  }
}

export const toDecimal = (value: string | number | undefined | Decimal): Decimal => {
  try {
    if (value instanceof Decimal)
      return value

    if (!value)
      return new Decimal(0)
    // If undefined, return 0 instead

    const floatNumber = Number.parseFloat(value.toString())

    if (Number.isNaN(floatNumber))
      throw new TypeError(`${ value } cannot be number!`)
    // If unknown number input throw error

    return new Decimal(`${ floatNumber }`)
  } catch (error) {
    throw new Error(`toDecimal error: ${ error.message }, stack: ${ error.stack }`)
  }
}

export { Decimal }

export const toDecimalNumber = (value: number, places = 6) => (new Decimal(value))
  .toDP(places, Decimal[`ROUND_UP`])
  .toFixed(places, Decimal[`ROUND_UP`]).toString()
